// client/src/contexts/NotificationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchNotifications as apiFetchNotifications,
  fetchUnreadCount as apiFetchUnreadCount,
  fetchAgentSummary as apiFetchAgentSummary,
  markAsRead as apiMarkAsRead,
  markAllAsRead as apiMarkAllAsRead,
  deleteNotification as apiDeleteNotification
} from '../services/notificationAPI';
import { useAuth } from './AuthContext';
import websocketService from '../services/websocketService';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

const POLL_INTERVAL = 60000; // 60 seconds

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [agentSummary, setAgentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { isAuthenticated } = useAuth();
  const pollIntervalRef = useRef(null);

  // ── API Actions ──────────────────────────────────────────────────────

  // Fetch paginated notifications
  const fetchNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const data = await apiFetchNotifications(params);
      setNotifications(data.notifications || data);
      setError(null);
      return data;
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Lightweight poll — only fetches the unread count
  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await apiFetchUnreadCount();
      setUnreadCount(data.count ?? 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Mark a single notification as read (optimistic update + API call)
  const markAsRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => (n._id === id ? { ...n, read: true } : n))
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await apiMarkAsRead(id);
    } catch (err) {
      console.error('Error marking notification as read:', err);
      // Revert on failure
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, read: false } : n))
      );
      setUnreadCount(prev => prev + 1);
      setError('Failed to mark notification as read.');
    }
  }, []);

  // Mark all notifications as read (optimistic update + API call)
  const markAllAsRead = useCallback(async () => {
    // Snapshot for rollback
    const prevNotifications = [...notifications];
    const prevCount = unreadCount;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await apiMarkAllAsRead();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      // Revert on failure
      setNotifications(prevNotifications);
      setUnreadCount(prevCount);
      setError('Failed to mark all notifications as read.');
    }
  }, [notifications, unreadCount]);

  // Delete / dismiss a notification
  const dismissNotification = useCallback(async (id) => {
    // Snapshot for rollback
    const prevNotifications = [...notifications];
    const wasUnread = notifications.find(n => n._id === id && !n.read);

    // Optimistic update
    setNotifications(prev => prev.filter(n => n._id !== id));
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      await apiDeleteNotification(id);
    } catch (err) {
      console.error('Error deleting notification:', err);
      // Revert on failure
      setNotifications(prevNotifications);
      if (wasUnread) {
        setUnreadCount(prev => prev + 1);
      }
      setError('Failed to delete notification.');
    }
  }, [notifications]);

  // Fetch weekly agent summary
  const fetchAgentSummary = useCallback(async () => {
    try {
      const data = await apiFetchAgentSummary();
      setAgentSummary(data);
      setError(null);
      return data;
    } catch (err) {
      console.error('Error fetching agent summary:', err);
      setError('Failed to load agent summary.');
    }
  }, []);

  // ── Initial Fetch + Polling ──────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset state when user logs out
      setNotifications([]);
      setUnreadCount(0);
      setAgentSummary(null);
      setError(null);
      return;
    }

    // Initial unread count fetch
    refreshUnreadCount();

    // Poll unread count every 60s as fallback for when WebSocket is unavailable
    pollIntervalRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, refreshUnreadCount]);

  // ── WebSocket Integration ────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    // Handle incoming real-time notification
    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    };

    // Handle remote "read" event (e.g. read on another device/tab)
    const handleReadNotification = (data) => {
      const id = data?.id || data?._id;
      if (id) {
        setNotifications(prev =>
          prev.map(n => (n._id === id ? { ...n, read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    };

    // Handle remote "read-all" event
    const handleReadAllNotifications = () => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    };

    // Register socket.io event listeners when socket is available
    const setupSocketListeners = () => {
      const socket = websocketService.socket;
      if (socket) {
        socket.on('notification:new', handleNewNotification);
        socket.on('notification:read', handleReadNotification);
        socket.on('notification:read-all', handleReadAllNotifications);
      }
    };

    setupSocketListeners();

    // Re-register after reconnection
    const unsubConnected = websocketService.on('connected', setupSocketListeners);
    const unsubReconnected = websocketService.on('reconnected', setupSocketListeners);

    return () => {
      unsubConnected();
      unsubReconnected();
      const socket = websocketService.socket;
      if (socket) {
        socket.off('notification:new', handleNewNotification);
        socket.off('notification:read', handleReadNotification);
        socket.off('notification:read-all', handleReadAllNotifications);
      }
    };
  }, [isAuthenticated]);

  // ── Context Value ────────────────────────────────────────────────────

  const value = {
    notifications,
    unreadCount,
    agentSummary,
    loading,
    error,
    fetchNotifications,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    fetchAgentSummary
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
